import { VariantUpdatedEvent, VariantSelectedEvent } from '@theme/events';
// import { morph } from '@theme/morph';

class VariantPicker extends HTMLElement {
  // Contrôleur pour annuler les requêtes en cours
  #abortController;

  #onChange = this.variantChanged.bind(this);
  #onClick = this.selectModelShape.bind(this);
  #focusShape;

  connectedCallback() {
    this.addEventListener('change', this.#onChange);
    this.addEventListener('click', this.#onClick);
    this.sortModelDimensions();
    this.updateModelLighting();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('click', this.#onClick);
    this.#abortController?.abort();
  }

  // Le format reste une seule option Shopify ; la forme filtre ses dimensions.
  selectModelShape(event) {
    const button = event.target.closest?.('[data-format-shape]');
    if (!button || !this.contains(button)) return;
    const formats = button.closest('[data-model-formats]');
    const inputs = [...formats.querySelectorAll('[data-dimension-shape]')]
      .filter(item => item.dataset.dimensionShape === button.dataset.formatShape)
      .map(item => item.querySelector('input'));
    const currentDetail = formats.querySelector('input:checked')?.dataset.variantDetail;
    const input = inputs.find(item => item.checked) ||
      (currentDetail && inputs.find(item => item.dataset.variantDetail === currentDetail && item.getAttribute('aria-disabled') !== 'true')) ||
      inputs.find(item => item.getAttribute('aria-disabled') !== 'true') || inputs[0];
    if (!input || input.checked) return;
    this.#focusShape = button.dataset.formatShape;
    input.checked = true;
    input.dispatchEvent(new Event('change', {bubbles:true}));
  }

  sortModelDimensions() {
    for (const container of this.querySelectorAll('.ml-dimensions')) {
      [...container.children].sort((a,b) => Number(a.dataset.diameter) - Number(b.dataset.diameter))
        .forEach(item => container.append(item));
    }
  }

  get modelVariants() {
    const source = this.querySelector('[data-model-variants]');
    return source ? JSON.parse(source.textContent) : [];
  }

  // Seules les températures réellement proposées pour ce coloris et ce format sont activées.
  updateModelLighting() {
    const variants = this.modelVariants;
    const fieldset = this.querySelector('fieldset[data-option-name="Éclairage"]');
    if (!variants.length || !fieldset) return;
    const fields = [...this.querySelectorAll('fieldset[data-option-position]')];
    const position = Number(fieldset.dataset.optionPosition) - 1;
    const matches = variants.filter(variant => fields.every(field =>
      field === fieldset || variant.options[Number(field.dataset.optionPosition) - 1] === field.querySelector('input:checked')?.value));
    for (const input of fieldset.querySelectorAll('input[data-option-value-id]')) {
      input.disabled = !matches.some(variant => variant.options[position] === input.value);
    }
    const lights = [...new Set(matches.map(variant => variant.options[position]))];
    fieldset.querySelector('.ml-lighting-note')?.remove();
    if (lights.length === 1) {
      const note = document.createElement('p');
      note.className = 'ml-lighting-note';
      note.textContent = 'Ce format est proposé uniquement en ' + lights[0].toLocaleLowerCase('fr') + '.';
      fieldset.append(note);
    }
  }

  // Changer de format ne doit jamais envoyer une combinaison absente de Shopify.
  resolveModelLighting(target) {
    const variants = this.modelVariants;
    if (!variants.length || target.closest('fieldset')?.dataset.optionName !== 'Format') return;
    const fields = [...this.querySelectorAll('fieldset[data-option-position]')];
    const matches = variant => fields.every(field =>
      variant.options[Number(field.dataset.optionPosition) - 1] === field.querySelector('input:checked')?.value);
    if (variants.some(matches)) return;
    const candidates = variants.filter(variant => fields.every(field =>
      field.dataset.optionName === 'Éclairage' || variant.options[Number(field.dataset.optionPosition) - 1] === field.querySelector('input:checked')?.value));
    const replacement = candidates.find(variant => variant.available) || candidates[0];
    const lighting = fields.find(field => field.dataset.optionName === 'Éclairage');
    if (!replacement || !lighting) return;
    const value = replacement.options[Number(lighting.dataset.optionPosition) - 1];
    for (const input of lighting.querySelectorAll('input')) input.checked = input.value === value;
  }

  /**
   * Gère l'événement de changement de variante.
   * @param {Event} event - L'événement de changement de variante.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (!event.target.matches('input[data-option-value-id], select')) return;
    const resolvedVariant = this.resolveVariantCombination(event.target);
    this.resolveModelLighting(event.target);

    // Met à jour l'option sélectionnée dans l'interface
    this.updateSelectedOption(event.target);

    // Déclenche un événement personnalisé pour notifier la sélection
    this.dispatchEvent(new VariantSelectedEvent({ id: event.target.dataset.optionValueId ?? '' }));

    // Lance la requête pour mettre à jour la section
    this.fetchUpdatedSection(this.buildRequestUrl());

    // Met à jour l'URL avec l'ID de variante sur les pages produit
    this.updateUrl(event.target, resolvedVariant?.id);
  }

  // Conserver le choix changé et retrouver une combinaison réellement vendue.
  resolveVariantCombination(target) {
    const source = this.querySelector('[data-variant-matrix]');
    const changedField = target.closest('fieldset[data-option-position]');
    if (!source || !changedField) return;
    const variants = JSON.parse(source.textContent);
    const fields = [...this.querySelectorAll('fieldset[data-option-position]')];
    const selected = field => field.querySelector('input:checked')?.value;
    const position = field => Number(field.dataset.optionPosition) - 1;
    if (variants.some(variant => fields.every(field =>
      variant.options[position(field)] === selected(field)))) return;
    const candidates = variants.filter(variant =>
      variant.options[position(changedField)] === target.value);
    const available = candidates.filter(variant => variant.available);
    const pool = available.length ? available : candidates;
    const score = variant => fields.filter(field => field !== changedField &&
      variant.options[position(field)] === selected(field)).length;
    const replacement = pool.reduce((best, variant) =>
      !best || score(variant) > score(best) ? variant : best, null);
    if (!replacement) return;
    for (const field of fields) {
      const value = replacement.options[position(field)];
      for (const input of field.querySelectorAll('input[data-option-value-id]')) {
        input.checked = input.value === value;
      }
    }
    return replacement;
  }

  // Met à jour l'option sélectionnée dans l'interface.
  updateSelectedOption(target) {
    // Si c'est une chaîne, trouve l'élément correspondant pour récupérer l'ID de la valeur d'option
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    // Met à jour l'état "checked" pour les boutons radio
    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    // Met à jour l'attribut "selected" pour les éléments select
    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      // Supprime l'attribut selected de toutes les options
      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      // Ajoute l'attribut selected à la nouvelle option
      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  // Construit l'URL de requête pour récupérer les données de variante.
  buildRequestUrl() {
    let productUrl = this.dataset.productUrl;

    // Le brouillon garde son contexte signé au lieu de dépendre du dernier cookie d'aperçu.
    const previewUrl = new URL(window.location.href);
    if (previewUrl.pathname === '/products_preview' && previewUrl.searchParams.has('preview_key')) {
      const previewParams = new URLSearchParams({
        preview_key: previewUrl.searchParams.get('preview_key'),
        option_values: this.selectedOptionsValues.join(','),
      });
      return `${previewUrl.pathname}?${previewParams}`;
    }

    // On utilise la nouvelle API de Shopify pour récupérer les données de variante avec option_values.
    const params = [];
    params.push(`option_values=${this.selectedOptionsValues.join(',')}`);

    // Si variant-picker est un enfant de quick-add-component ou swatches-variant-picker-component,
    // nous devons ajouter section_id=section-rendering-product-card à l'URL
    // if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
    //   if (productUrl?.includes('?')) {
    //     productUrl = productUrl.split('?')[0];
    //   }
    //   return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    // }
    return `${productUrl}?${params.join('&')}`;
  }

  // Récupère la section mise à jour depuis le serveur.
  fetchUpdatedSection(requestUrl) {
    // Annule la requête fetch précédente si elle est encore en cours
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');

        // Récupère les données JSON de la variante depuis la réponse
        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        // Met à jour seulement le variant picker
        this.updateVariantPicker(html);

        // Déclenche un événement pour notifier la mise à jour de la variante
        if (this.selectedOptionId) {
          this.dispatchEvent(
            new VariantUpdatedEvent(JSON.parse(textContent), this.selectedOptionId, {
              html,
              productId: this.dataset.productId ?? '',
            }),
          );
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.log("Fetch interrompu par l'utilisateur");
        } else {
          console.error(error);
        }
      });
  }

  // Re-rend le sélecteur de variante avec les nouvelles données.
  updateVariantPicker(newHtml) {
    // Trouve le nouveau variant picker dans la réponse
    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('Aucune nouvelle source de variant picker trouvée');
    }

    // Met à jour le variant picker
    const focusedId = this.contains(document.activeElement) ? document.activeElement.id : null;
    this.innerHTML = newVariantPickerSource.innerHTML;
    this.sortModelDimensions();
    this.updateModelLighting();
    const focusTarget = this.#focusShape ?
      [...this.querySelectorAll('[data-format-shape]')].find(button => button.dataset.formatShape === this.#focusShape) :
      (focusedId ? this.querySelector('#' + CSS.escape(focusedId)) : null);
    focusTarget?.focus({preventScroll:true});
    this.#focusShape = null;
  }

  // Met à jour l'URL avec l'ID de variante sur les pages produit
  updateUrl(target, resolvedVariantId) {
    // Détermine si nous sommes sur une page produit (pas dans une carte produit ou dialogue)
    const isOnProductPage =
      Theme.template.name === 'product' &&
      !target.closest('product-card') &&
      !target.closest('quick-add-dialog');

    // Prépare l'URL pour la mise à jour de l'historique du navigateur
    const url = new URL(window.location.href);

    let variantId;

    // Extrait l'ID de la variante selon le type d'élément (radio ou select)
    if (target instanceof HTMLInputElement && target.type === 'radio') {
      variantId = resolvedVariantId || target.dataset.variantId || null;
    } else if (target instanceof HTMLSelectElement) {
      const selectedOption = target.options[target.selectedIndex];
      variantId = selectedOption?.dataset.variantId || null;
    }

    // Met à jour l'URL avec l'ID de variante sur les pages produit
    if (isOnProductPage) {
      url.searchParams.delete('option_values');
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Met à jour l'URL dans l'historique du navigateur si elle a changé
    if (url.href !== window.location.href) {
      history.replaceState({}, '', url.toString());
    }
  }

  // Récupère l'option actuellement sélectionnée.
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  // Récupère l'ID de l'option actuellement sélectionnée.
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error("Aucun ID de valeur d'option trouvé");
    }

    return optionValueId;
  }

  // Récupère toutes les valeurs d'options actuellement sélectionnées.
  get selectedOptionsValues() {
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error("Aucun ID de valeur d'option trouvé");

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}

